import { useQueryClient } from '@tanstack/react-query';
import { Activity, ChevronsLeft, ChevronsRight, LogOut, Menu, Moon, Redo2, Search, Sun, Undo2 } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { forgetMe, useMe, useOverview } from '../api/hook.js';
import { initials } from '../lib/format.js';
import { useActiveMemento } from '../memento/view-state.js';
import { APP_VERSION } from '../release/release.js';
import { CommandPalette } from './CommandPalette.js';
import { NAV_ITEM, NAVIGATION } from './navigation.js';

function readPreference(key: string, fallback: string) {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writePreference(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* preference only */
  }
}

const isTyping = (target: EventTarget | null) => target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

export function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const overview = useOverview();
  const me = useMe();
  const memento = useActiveMemento();
  const client = useQueryClient();
  const [palette, setPalette] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readPreference('monitor.sidebar', 'open') === 'collapsed');
  const [mobile, setMobile] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme ?? 'dark');
  const [menu, setMenu] = useState(false);
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => tick((value) => value + 1), 10_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey;
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalette(true);
        return;
      }
      if (isTyping(event.target)) return;
      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) memento.redo();
        else memento.undo();
      } else if (mod && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        memento.redo();
      } else if (event.key === '/') {
        event.preventDefault();
        setPalette(true);
      } else if (event.altKey && /^[0-9]$/.test(event.key)) {
        const target = NAV_ITEM.find((item) => item.shortcut === event.key);
        if (target) navigate(target.path);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [memento, navigate]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    writePreference('monitor.theme', next);
    setTheme(next);
  };

  const toggleSidebar = () => {
    setCollapsed((value) => {
      writePreference('monitor.sidebar', value ? 'open' : 'collapsed');
      return !value;
    });
  };

  const logout = async () => {
    const result = await api<{ redirect: string }>('/auth/sso/logout', { method: 'POST' }).catch(() => null);
    forgetMe();
    if (result?.redirect) window.location.assign(result.redirect);
  };

  const data = overview.data;
  const badge: Record<string, { value: number; tone: 'critical' | 'warning' | '' } | undefined> = data
    ? {
        '/alert': (() => {
          const open = Object.values(data.kpi.openAlert).reduce((a, b) => a + b, 0);
          return open ? { value: open, tone: data.kpi.openAlert.Critical ? 'critical' : 'warning' } : undefined;
        })(),
        '/health': data.applicationState.Unhealthy + data.applicationState.Degraded
          ? { value: data.applicationState.Unhealthy + data.applicationState.Degraded, tone: data.applicationState.Unhealthy ? 'critical' : 'warning' }
          : undefined,
        '/error': (() => {
          const hot = data.errorHotspot.filter((item) => item.classification === 'Spiking' || item.classification === 'Regression' || item.classification === 'New').length;
          return hot ? { value: hot, tone: 'critical' } : undefined;
        })(),
        '/provider': data.providerState.Disconnected ? { value: data.providerState.Disconnected, tone: 'critical' } : undefined,
        '/cost': data.costAnomaly.length ? { value: data.costAnomaly.length, tone: 'warning' } : undefined,
      }
    : {};

  const current = NAV_ITEM.find((item) => location.pathname.startsWith(item.path));
  const updated = overview.dataUpdatedAt ? Math.round((Date.now() - overview.dataUpdatedAt) / 1000) : null;

  return (
    <div className={`shell${collapsed ? ' collapsed' : ''}${mobile ? ' mobile-open' : ''}`}>
      <aside className="sidebar" aria-label="Navigazione">
        <Link to="/overview" className="brand">
          <span className="brand-mark">
            <Activity size={15} strokeWidth={2.5} />
          </span>
          <span className="brand-name">Monitor</span>
          {me.data?.mode === 'mock' ? <span className="brand-env" data-tip="Dati simulati: integrazioni reali non ancora attive">Mock</span> : null}
        </Link>
        <nav className="nav">
          {NAVIGATION.map((group) => (
            <div className="nav-group" key={group.label ?? 'main'}>
              {group.label ? <div className="nav-group-label">{group.label}</div> : null}
              {group.item.map((item) => {
                const value = badge[item.path];
                return (
                  <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} data-tip={`${item.label}: ${item.hint} (Alt+${item.shortcut})`}>
                    <item.icon size={16} />
                    <span>{item.label}</span>
                    {value ? <b className={`nav-badge ${value.tone}`}>{value.value}</b> : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <button type="button" className="btn ghost icon hide-mobile" onClick={toggleSidebar} aria-label={collapsed ? 'Espandi il menu' : 'Comprimi il menu'}>
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
          <div className="sidebar-foot-text">
            <span className="mono">v{APP_VERSION}</span>
            <Link to="/release" className="muted">
              Note di rilascio
            </Link>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button type="button" className="btn ghost icon menu-button" onClick={() => setMobile((value) => !value)} aria-label="Menu">
            <Menu size={18} />
          </button>
          <div className="crumb hide-mobile">
            {current?.group ? <span>{current.group}</span> : null}
            {current?.group ? <span className="dim">/</span> : null}
            <strong>{current?.label ?? (location.pathname.startsWith('/release') ? 'Note di rilascio' : 'Monitor')}</strong>
          </div>
          <button type="button" className="topbar-search" onClick={() => setPalette(true)}>
            <Search size={15} />
            <span>Cerca o incolla un CorrelationId</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="topbar-actions">
            <button type="button" className="btn ghost icon" disabled={!memento.canUndo} onClick={memento.undo} data-tip="Annulla l’ultima modifica della vista (Ctrl+Z)" aria-label="Annulla">
              <Undo2 size={16} />
            </button>
            <button type="button" className="btn ghost icon" disabled={!memento.canRedo} onClick={memento.redo} data-tip="Ripeti (Ctrl+Shift+Z)" aria-label="Ripeti">
              <Redo2 size={16} />
            </button>
            <button
              type="button"
              className={`live${overview.isError ? ' paused' : ''}`}
              onClick={() => client.invalidateQueries()}
              data-tip="Aggiornamento automatico ogni 30 secondi mentre la scheda è visibile. Clic per aggiornare ora."
            >
              <i className="live-dot" />
              <span>{overview.isFetching ? 'Aggiorno…' : updated !== null ? `Live (${updated < 5 ? 'ora' : `${updated} s fa`})` : 'Live'}</span>
            </button>
            <button type="button" className="btn ghost icon" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'} data-tip={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div style={{ position: 'relative' }}>
              <button type="button" className="btn ghost icon" onClick={() => setMenu((value) => !value)} aria-label="Account" aria-expanded={menu}>
                <span className="avatar">{initials(me.data?.name ?? '?')}</span>
              </button>
              {menu ? (
                <div className="dialog" style={{ position: 'absolute', right: 0, top: 38, width: 240, zIndex: 40 }} onMouseLeave={() => setMenu(false)}>
                  <div className="dialog-body" style={{ gap: 4 }}>
                    <strong>{me.data?.name}</strong>
                    <span className="muted">Ruolo in Monitor: {me.data?.role}</span>
                    <span className="dim" style={{ fontSize: 11.5 }}>
                      Accesso gestito da security.miglioresoftware.com
                    </span>
                  </div>
                  <div className="dialog-foot">
                    <button type="button" className="btn sm" onClick={logout} disabled={me.data?.mode === 'mock'}>
                      <LogOut size={14} />
                      Esci
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main className="content" id="content">
          <Suspense fallback={<div className="page"><div className="skeleton" style={{ height: 240 }} /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {palette ? <CommandPalette onClose={() => setPalette(false)} /> : null}
    </div>
  );
}
